import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Loading, SystemText, Table, SaveButton, DebugInfo, Tabs } from '@salesperformanceint/e4shared';

import PageHeader from 'components/general/pageHeader';
import NoResults from 'components/general/noResults';

import { apiPost, apiDownloadFile } from 'services';
import { e4Format, e4Formats, differenceInCalendarDays } from 'helpers/dates';
import SearchField from 'components/general/searchField';
import { setAsideFilters, toggleFilterAside } from 'actions/ui';
import posthog from 'posthog-js';
import { skillColour } from 'helpers/metrics';
import Skills from './components/skills';
import QuickCheckSkills from './components/quickcheckskills';
import { translateCourse } from 'helpers/translations';

export const LearnTracker = () => {
    const dispatch = useDispatch();
    const [loadingLearning, setLoadingLearning] = React.useState(false);
    const [loadingQuickCheck, setLoadingQuickCheck] = React.useState(false);
    const [searchFilter, setSearchFilter] = React.useState('');

    const [showResults, setShowResults] = React.useState(false);
    const [showQuickCheckResults, setShowQuickCheckResults] = React.useState(false);
    const [page, setPage] = React.useState(1);
    const [sortBy, setSortBy] = React.useState('full_Name');
    const [descending, setDescending] = React.useState(false);
    const [learningData, setLearningData] = React.useState({});
    const [quickCheckData, setQuickCheckData] = React.useState({});

    const [downloadingLearningReport, setDownloadingLearningReport] = React.useState(false);
    const [downloadingQuickCheckReport, setDownloadingQuickCheckReport] = React.useState(false);

    const { filterAsideOpen, asideFilters } = useSelector(state => state.ui);
    const user = useSelector(state => state.user.user);
    const allowCoaches = user.companySettings.development.allowCoaches;

    const isPrism = user?.companySettings?.prism_UI;

    const [tab, setTab] = React.useState('learning');

    const api = {
        loadLearningData: (callback, extraHeaders) => {
            setLoadingLearning(true);
            const filters = asideFilters
                .map(x => ({ property: x.property, value: x.value }))
                .concat([{ property: 'search', value: searchFilter }]);

            const qs = `?page=${page}&sort=${sortBy}&desc=${descending}`;

            apiPost(
                `learning-dashboard${qs}`,
                filters,
                user.guid,
                response => {
                    const translated = response.assignments.map(c => {
                        const nc = translateCourse(user.language_Code, c);
                        nc.title = nc.name;
                        return nc;
                    });

                    setLearningData({ ...response, assignments: translated });
                    callback?.();
                },
                null,
                'POST',
                'json',
                extraHeaders
            );
        },
        loadQuickCheckData: (callback, extraHeaders) => {
            setLoadingQuickCheck(true);
            const filters = asideFilters
                .map(x => ({ property: x.property, value: x.value }))
                .concat([{ property: 'search', value: searchFilter }]);

            const qs = `?page=${page}&sort=${sortBy}&desc=${descending}`;
            apiPost(
                `learning-dashboard-quickcheck${qs}`,
                filters,
                user.guid,
                response => {
                    const translated = response.items.map(c => {
                        const nc = translateCourse(user.language_Code, c);
                        nc.title = nc.name;
                        return nc;
                    });
                    setQuickCheckData({ ...response, assignments: translated });
                    callback?.();
                },
                null,
                'POST',
                'json',
                extraHeaders
            );
        },
        learningRreport: () => {
            const filters = asideFilters
                .map(x => ({ property: x.property, value: x.value }))
                .concat([{ property: 'search', value: searchFilter }]);

            apiDownloadFile(
                'learning-report',
                filters,
                user.guid,
                () => {
                    setDownloadingLearningReport(false);
                    posthog.capture('tracker', { trackerType: 'LearnTracker', userEvent: 'export' });
                },
                null,
                'POST'
            );
        },
        quickCheckReport: () => {
            const filters = [{ property: 'search', value: searchFilter }];
            apiDownloadFile(
                'quickcheck-report',
                filters,
                user.guid,
                () => {
                    setDownloadingQuickCheckReport(false);
                    posthog.capture('tracker', { trackerType: 'LearnQuickCheckTracker', userEvent: 'export' });
                },
                null,
                'POST'
            );
        },
    };

    const changeTab = newTab => {
        setTab(newTab);
        setPage(1);
        setSortBy('full_Name');
        setDescending(false);
    };

    const daysRemainingColour = days => {
        if (days < 12) return 'danger';
        if (days < 25) return 'warning-danger';
        if (days < 37) return 'warning';
        if (days < 48) return 'success-warning';
        return 'success';
    };

    const tabDefinitions = {
        learning: {
            columns: [
                {
                    label: SystemText('LearningTracker/Table/Member'),
                    dataProperty: 'full_Name',
                },
                {
                    label: SystemText('LearningTracker/Table/Course'),
                    dataProperty: 'title',
                },
                {
                    label: SystemText('LearningTracker/Table/Status'),
                    dataProperty: 'status',
                    format: x =>
                        x === 'completed' ? (
                            <label className="label label-success">{SystemText('LearningTracker/Buttons/Completed')}</label>
                        ) : (
                            <label className="label label-warning">{SystemText('LearningTracker/Buttons/InProgress')}</label>
                        ),
                },
                {
                    label: SystemText('LearningTracker/Table/Progress'),
                    dataProperty: 'score_Percent',
                    format: x => <label className="label label-light-grey">{parseInt(x, 10)}%</label>,
                },
                {
                    label: SystemText('LearningTracker/Table/Started'),
                    dataProperty: 'started_At',
                    format: x => e4Format(x, e4Formats.default, false, '-'),
                },
                {
                    label: SystemText('LearningTracker/Table/LastActivity'),
                    dataProperty: 'last_Activity_At',
                    format: x => e4Format(x, e4Formats.default, false, '-'),
                },
                {
                    label: SystemText('LearningTracker/Table/Completed'),
                    dataProperty: 'completed_At',

                    format: x => e4Format(x, e4Formats.default, false, '-'),
                },
                {
                    label: SystemText('LearningTracker/Table/Results'),
                    dataProperty: 'skills_Score',
                    format: (x, d) =>
                        !x && x !== 0 ? (
                            <span>-</span>
                        ) : (
                            <button className={`btn btn-xs btn-light-${skillColour(x)}`} onClick={() => setShowResults(d)}>
                                {x}%
                            </button>
                        ),
                },
                {
                    label: SystemText('LearningTracker/Table/Results'),
                    dataProperty: 'examResults',
                    format: (x, d) =>
                        (x && x > 0) || (d.skills_Score && d.skills_Score !== 0) || x?.length > 0 ? (
                            <button className="btn btn-success" onClick={() => setShowResults(d)}>
                                View
                            </button>
                        ) : (
                            <span>-</span>
                        ),
                },
            ],
            options: {
                sortBy,
                ascending: !descending,
                pageSize: 20,
                recordCount: learningData.recordcount,
                page,
                tableClass: 'table table-striped',
            },
        },
        quickcheck: {
            columns: [
                {
                    label: SystemText('LearningTracker/QuickCheck/Member'),
                    dataProperty: 'full_Name',
                },
                {
                    label: SystemText('LearningTracker/QuickCheck/EnrollmentName'),
                    dataProperty: 'title',
                },
                {
                    label: SystemText('LearningTracker/QuickCheck/Started'),
                    dataProperty: 'started_At',
                    format: x => e4Format(x, e4Formats.default, false, '-'),
                },
                {
                    label: SystemText('LearningTracker/QuickCheck/DaysRemaining'),
                    dataProperty: 'expirationDate',
                    format: x => {
                        let days = differenceInCalendarDays(x, new Date());
                        return <label className={`label label-${daysRemainingColour(days)}`}>{days < 0 ? 0 : days}</label>;
                    },
                },
                {
                    label: SystemText('LearningTracker/QuickCheck/LastResponse'),
                    dataProperty: 'last_Activity_At',
                    format: x => e4Format(x, e4Formats.default, false, '-'),
                },
                {
                    label: SystemText('LearningTracker/QuickCheck/QuestionsTotal'),
                    dataProperty: 'totalQuestions',
                },
                {
                    label: SystemText('LearningTracker/QuickCheck/QuestionsInProgress'),
                    dataProperty: 'questionsInProgress',
                },
                {
                    label: SystemText('LearningTracker/QuickCheck/QuestionsRetired'),
                    dataProperty: 'questionsRetired',
                },
                {
                    label: SystemText('LearningTracker/QuickCheck/QuestionsMastered'),
                    dataProperty: 'questionsMastered',
                },
                {
                    label: SystemText('LearningTracker/Table/Results'),
                    dataProperty: 'skills',
                    format: (x, d) =>
                        x?.length > 0 ? (
                            <button className="btn btn-success" onClick={() => setShowQuickCheckResults(d)}>
                                View
                            </button>
                        ) : (
                            <span>-</span>
                        ),
                },
            ],
            options: {
                sortBy,
                ascending: !descending,
                pageSize: 20,
                recordCount: quickCheckData.recordcount,
                page,
                tableClass: 'table table-striped',
            },
        },
    };

    if (isPrism) {
        tabDefinitions.learning.columns = tabDefinitions.learning.columns.filter(x => x.dataProperty !== 'skills_Score');
    } else {
        tabDefinitions.learning.columns = tabDefinitions.learning.columns.filter(x => x.dataProperty !== 'examResults');
    }

    React.useEffect(() => {
        DebugInfo('component', 'LearnTracker');
        posthog.capture('tracker', { trackerType: 'LearnTracker', userEvent: 'view' });
    }, []);

    React.useEffect(() => {
        let fields = [
            {
                property: 'manager_Ids',
                name: SystemText('LearningTracker/Filters/Manager'),
                type: 'list',
                nameProperty: 'list_Name',
            },
            {
                property: 'area_Ids',
                name: SystemText('LearningTracker/Filters/Area'),
                type: 'list',
            },
            {
                property: 'competency_Ids',
                name: SystemText('LearningTracker/Filters/Competency'),
                type: 'list',
                nameProperty: 'full_Name',
            },
            {
                property: 'role_Ids',
                name: SystemText('LearningTracker/Filters/Role'),
                type: 'list',
            },
            {
                property: 'department_Ids',
                name: SystemText('LearningTracker/Filters/Department'),
                type: 'list',
            },
            {
                property: 'business_Unit_Ids',
                name: SystemText('LearningTracker/Filters/BusinessUnit'),
                type: 'list',
            },
            {
                property: 'location_Ids',
                name: SystemText('LearningTracker/Filters/Location'),
                type: 'list',
            },
            {
                property: 'language_Ids',
                name: SystemText('LearningTracker/Filters/Language'),
                type: 'list',
            },
            {
                property: 'statuses',
                name: SystemText('LearningTracker/Filters/Status'),
                type: 'list',
                stringValues: true,
                data: [
                    { id: 'P', name: SystemText('LearningTracker/Filters/Status/InProgress') },
                    { id: 'C', name: SystemText('LearningTracker/Filters/Status/Complete') },
                ],
            },
            {
                property: 'start_Date',
                name: SystemText('LearningTracker/Filters/Start'),
                type: 'range',
            },
            {
                property: 'completed_Date',
                name: SystemText('LearningTracker/Filters/Completed'),
                type: 'range',
            },
        ];

        if (!allowCoaches) {
            fields = fields.filter(x => x.property !== 'coach_Ids');
        }

        dispatch(setAsideFilters(fields));
    }, []);

    React.useEffect(() => {
        if (tab !== 'learning') return;

        const ac = new AbortController();

        api.loadLearningData(() => setLoadingLearning(false), { signal: ac.signal });

        return () => {
            ac.abort();
        };
    }, [filterAsideOpen, searchFilter, sortBy, descending, page, tab]);

    React.useEffect(() => {
        if (tab !== 'quickcheck') return;

        const ac = new AbortController();

        api.loadQuickCheckData(() => setLoadingQuickCheck(false), { signal: ac.signal });

        return () => {
            ac.abort();
        };
    }, [searchFilter, page, tab, sortBy, descending]);

    React.useEffect(() => {
        if (!filterAsideOpen) {
            setPage(1);
        }
    }, [filterAsideOpen]);

    React.useEffect(() => {
        setPage(1);
    }, [searchFilter, sortBy, descending]);

    const triggerSort = React.useCallback(
        column => {
            column === sortBy && setDescending(!descending);
            setSortBy(column);
        },
        [sortBy, descending]
    );

    const showFilterMenu = () => {
        dispatch(toggleFilterAside(true));
    };

    const learnTab = (
        <div>
            {loadingLearning ? (
                <Loading />
            ) : !learningData?.recordcount ? (
                <div>
                    <NoResults
                        title={SystemText('LearningTracker/NoResults/Title')}
                        message={SystemText('LearningTracker/NoResults/Message')}
                    />
                </div>
            ) : (
                <Table
                    data={learningData?.assignments}
                    columns={tabDefinitions.learning.columns}
                    options={tabDefinitions.learning.options}
                    sort={triggerSort}
                    goToPage={setPage}
                />
            )}
        </div>
    );

    const quickCheckTab = (
        <div>
            {loadingQuickCheck ? (
                <Loading />
            ) : !quickCheckData?.totalItems ? (
                <div>
                    <NoResults
                        title={SystemText('LearningTracker/NoResults/Title')}
                        message={SystemText('LearningTracker/NoResults/Message')}
                    />
                </div>
            ) : (
                <Table
                    data={quickCheckData?.items}
                    columns={tabDefinitions.quickcheck.columns}
                    options={tabDefinitions.quickcheck.options}
                    sort={triggerSort}
                    goToPage={setPage}
                />
            )}
        </div>
    );

    const tabSettings = isPrism
        ? {
              availableTabs: ['learning', 'quickcheck'],
              labels: {
                  learning: SystemText('LearningTracker/Tabs/Learning'),
                  quickcheck: SystemText('LearningTracker/Tabs/QuickCheck'),
              },
          }
        : { availableTabs: ['learning'], labels: {} };

    return (
        <div>
            <PageHeader title={SystemText('LearningTracker/Title')} tracker />
            <section className="content mtm10" data-testid="learn-tracker-content">
                <Box>
                    <div className="mb10">
                        <SearchField
                            value={searchFilter}
                            onChange={setSearchFilter}
                            placeholder={SystemText('LearningTracker/Search')}
                        >
                            {(user.administrator || user.isManager) && (
                                <>
                                    {tab === 'learning' && (
                                        <SaveButton
                                            className="btn btn-info mr10"
                                            saving={downloadingLearningReport}
                                            setSaving={setDownloadingLearningReport}
                                            onClick={api.learningRreport}
                                        >
                                            <i className="fa fa-download mr5" />
                                            {SystemText('LearningTracker/Buttons/Report')}
                                        </SaveButton>
                                    )}
                                    {tab === 'quickcheck' && (
                                        <SaveButton
                                            className="btn btn-info mr10"
                                            saving={downloadingQuickCheckReport}
                                            setSaving={setDownloadingQuickCheckReport}
                                            onClick={api.quickCheckReport}
                                        >
                                            <i className="fa fa-download mr5" />
                                            {SystemText('LearningTracker/Buttons/Report')}
                                        </SaveButton>
                                    )}
                                </>
                            )}

                            {tab === 'learning' && (
                                <>
                                    <button className="btn btn-success" id="search-filter-button" onClick={showFilterMenu}>
                                        <i className="fa fa-bars-filter mr5" id="search-filter-button-icon" />
                                        {SystemText('LearningTracker/Buttons/Filter')}
                                    </button>
                                </>
                            )}
                        </SearchField>
                    </div>

                    <div>
                        <div className="pr5 pl5">
                            <Tabs
                                role="tabpanel"
                                tab={tab}
                                setTab={changeTab}
                                tabs={tabSettings.availableTabs}
                                style="clean"
                                tabNames={tabSettings.labels}
                                extras={null}
                            >
                                <div className="mt10">{tab === 'learning' ? learnTab : quickCheckTab}</div>
                            </Tabs>
                        </div>
                    </div>
                </Box>
            </section>
            <Skills show={showResults} close={() => setShowResults(false)} hideBehaviour={isPrism} />
            <QuickCheckSkills show={showQuickCheckResults} close={() => setShowQuickCheckResults(false)} />
        </div>
    );
};

export default LearnTracker;
